// React
import { useState } from "react";
import { flushSync } from "react-dom";
import PropTypes from "prop-types";
// MUI
import { Box, Grid, Stack, SvgIcon, Tooltip, Typography } from "@mui/material";

// Helpers
import { and, nullishResolver, or, renderIf, ternaryResolver } from "@Common/helpers/sonar";
// Icons
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import AddCircleRounded from "@mui/icons-material/AddCircleRounded";
import { ReactComponent as TrashIcon } from "@/assets/icons/common/trash-icon.svg";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

// Components
import SimpleIconButton from "../Buttons/SimpleIconButton";
import SimpleDoubleChecking from "../Buttons/SimpleDoubleChecking";
import SimpleTextField from "../InputComponents/SimpleTextField";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";

// ************************************* HELPERS FUNCTIONS ************************************** \\
function showAdd(entry, arrayLength, order) {
	return order + 1 >= arrayLength;
}

function showDelete(entry, arrayLength, order) {
	if (entry === "" && arrayLength === 1 && order === 0) {
		return false;
	}
	return arrayLength !== 1;
}

const getFieldError = ({ entry, items, showAdvancedSelect, advancedSelectError, isValidationFailed, textFieldFocused }) => {
	let textFieldError;
	if (showAdvancedSelect) {
		if (textFieldFocused) {
			textFieldError = isValidationFailed && items?.type;
		} else {
			textFieldError = isValidationFailed || !entry.accountType || advancedSelectError;
		}
	} else {
		textFieldError = isValidationFailed;
	}

	return textFieldError;
};

function CustomSmartSelector(props) {
	const { entry, advancedSelectError, setTextFieldFocused, setSmartSelectFocused } = props;
	return (
		<SmartSelector
			selectBy={"accountType"}
			options={props.accountTypes || []}
			selected={entry?.accountType ? { accountType: entry.accountType, label: entry.accountType } : null}
			inputProps={{
				placeholder: "Type",
			}}
			listBoxProps={{
				sx: { maxHeight: "220px" },
			}}
			error={!entry.accountType || Boolean(advancedSelectError)}
			sx={{
				width: "120px",
				marginLeft: "2px",
			}}
			onOpenAction={(event) => {
				event.preventDefault();
				flushSync(() => {
					setTextFieldFocused(true);
					setSmartSelectFocused(true);
				});
			}}
			onCloseAction={() => {
				flushSync(() => {
					setSmartSelectFocused(false);
					setTextFieldFocused(false);
				});
			}}
			onChangeAction={(value) => {
				props.onTypeChange(value, props.from, props.order);
				flushSync(() => {
					setSmartSelectFocused(false);
					setTextFieldFocused(false);
				});
			}}
		/>
	);
}

CustomSmartSelector.propTypes = {
	entry: PropTypes.object,
	from: PropTypes.string,
	order: PropTypes.number,
	advancedSelectError: PropTypes.string,
	accountTypes: PropTypes.array,
	setTextFieldFocused: PropTypes.func,
	setSmartSelectFocused: PropTypes.func,
	onTypeChange: PropTypes.func,
};

const getValidationFailed = (props, entry) =>
	(props.simpleValidation(entry, props.items) === false && props.validation(props.items) === false) || entry.error;

const getAdvancedSelectError = (entry, accountTypes) => {
	let advancedSelectError;
	if (entry.typeError) {
		const foundError = entry.typeError.find((error) => error.glAccount === entry.glAccount);
		if (foundError) {
			advancedSelectError = accountTypes?.find((type) => type.accountType === foundError.expectedType)?.label;
		} else {
			advancedSelectError = null;
		}
	}
	return advancedSelectError;
};

export default function MultipleTextFieldGroup(props) {
	const [textFieldFocused, setTextFieldFocused] = useState(false);
	const [smartSelectFocused, setSmartSelectFocused] = useState(false);
	let textFieldError = false;
	let advancedSelectError;

	return (
		<Grid container>
			{props.items?.length === 0 ? (
				<Grid item>
					<Stack direction="row" justifyContent={"flex-end"} alignItems="center" sx={{ height: "40px" }}>
						<SimpleTextField
							sx={props.sx || { width: "150px" }}
							editable={!props.readOnly}
							disabled={props.isLoading}
							placeholder={props.placeholder}
							outlined
							text={""}
							error={false}
							onActionUpdate={(value) => props.onActionUpdate(value, props.from, 0)}
							loading={props.isLoading}
							handleKeyDownText={props.handleKeyDownText || {}}
							tooltipMessage={props.tooltipMessage || ""}
							openTooltip={props.openTooltip}
						/>

						{showAdd("", props.items.length, 0) && !props.disabled && (
							<>
								<Box />
								<SimpleIconButton
									className={"btn-add"}
									color={"var(--primary-color)"}
									disabled={false}
									icon={<AddCircleOutlineRoundedIcon fontSize="medium" />}
									iconHover={<AddCircleRounded fontSize="medium" />}
									onClick={() => {
										props.onAdd(props.items);
									}}
								/>
							</>
						)}
					</Stack>
				</Grid>
			) : (
				props.items?.map((entry, order) => {
					const isValidationFailed = getValidationFailed(props, entry);
					advancedSelectError = getAdvancedSelectError(entry, props.accountTypes);

					textFieldError = getFieldError({
						entry,
						textFieldFocused,
						advancedSelectError,
						isValidationFailed,
						items: props.items,
						showAdvancedSelect: props.showAdvancedSelect,
					});

					const smartSelect = (
						<CustomSmartSelector
							{...props}
							order={order}
							entry={entry}
							setTextFieldFocused={setTextFieldFocused}
							setSmartSelectFocused={setSmartSelectFocused}
						/>
					);
					return (
						<Grid item key={`${order}`} className="" marginRight={0.9}>
							<Stack direction="row" justifyContent={"flex-end"} alignItems="center" sx={{ height: "40px" }}>
								{!props.hideTextField && (
									<SimpleTextField
										sx={nullishResolver(props.textFieldStyle, { fontSize: "25px", width: "150px" })}
										editable={!props.readOnly}
										disabled={props.isLoading}
										placeholder={props.placeholder}
										outlined
										text={ternaryResolver(
											Object.hasOwn(entry, "value"),
											nullishResolver(entry.value, ""),
											nullishResolver(entry, "")
										)}
										warning={or(props.warningList?.includes(entry?.value), props.warningList?.includes(entry))}
										error={textFieldError}
										onActionUpdate={(value) => props.onActionUpdate(value, props.from, order)}
										loading={props.isLoading}
										handleKeyDownText={props.handleKeyDownText || function () {}}
										tooltipMessage={props.tooltipMessage}
										openTooltip={and(props.openTooltip, entry.error)}
										onFocus={() => {
											setTextFieldFocused(true);
										}}
										onFocusOut={() => {
											if (!smartSelectFocused) {
												setTextFieldFocused(false);
											}
										}}
									/>
								)}

								{and(props.showAdvancedSelect, textFieldFocused, advancedSelectError) && (
									<Tooltip
										title={
											<Typography sx={{ fontFamily: "PTSansProNarrow", color: "white", fontSize: "14px" }}>
												{nullishResolver(advancedSelectError, "")}
											</Typography>
										}
										arrow
									>
										<div>{smartSelect}</div>
									</Tooltip>
								)}
								{and(props.showAdvancedSelect, textFieldFocused, !advancedSelectError) && <div>{smartSelect}</div>}
								{and(props.showDeleteInFirst, !showDelete(entry, props.items.length, order), !props.disabled) && (
									<>
										<Box sx={{ width: "3px" }} />
										<SimpleDoubleChecking
											className={"btn-delete"}
											disabled={props.deleting}
											color={"var(--gray-blue-color)"}
											confirmationButtonColor={"var(--gray-blue-color)"}
											icon={<CloseRoundedIcon fontSize="small" />}
											iconHover={<CloseRoundedIcon fontSize="small" />}
											confirmationIcon={
												<SvgIcon viewBox="0 0 20 20" className="!text-[20px] ml-[1px]" component={TrashIcon} />
											}
											onDeleteAction={() => {
												props.onDelete(order, props.from);
											}}
											onFocus={() => {
												setTextFieldFocused(true);
											}}
											onFocusOut={() => setTextFieldFocused(false)}
										/>
									</>
								)}
								{renderIf(
									and(showDelete(entry, props.items.length, order), !props.disabled),
									<>
										<Box sx={{ width: "3px" }} />
										<SimpleDoubleChecking
											className={"btn-delete"}
											disabled={props.deleting}
											color={"var(--gray-blue-color)"}
											confirmationButtonColor={"var(--gray-blue-color)"}
											icon={<CloseRoundedIcon fontSize="small" />}
											iconHover={<CloseRoundedIcon fontSize="small" />}
											confirmationIcon={
												<SvgIcon viewBox="0 0 20 20" className="!text-[20px] ml-[1px]" component={TrashIcon} />
											}
											onDeleteAction={() => {
												props.onDelete(order, props.from);
											}}
											onFocus={() => {
												setTextFieldFocused(true);
											}}
											onFocusOut={() => setTextFieldFocused(false)}
										/>
									</>
								)}
								{showAdd(entry, props.items.length, order) && !props.disabled && (
									<>
										<Box />
										<SimpleIconButton
											className={"btn-add"}
											color={"var(--primary-color)"}
											disabled={or(props.isLoading, props.disabledAddButton, false)}
											icon={<AddCircleOutlineRoundedIcon fontSize="medium" />}
											iconHover={<AddCircleRounded fontSize="medium" />}
											onClick={() => {
												props.onAdd(props.items);
											}}
											onFocus={() => {
												setTextFieldFocused(true);
											}}
											onFocusOut={() => setTextFieldFocused(false)}
										/>
									</>
								)}
							</Stack>
						</Grid>
					);
				})
			)}
		</Grid>
	);
}

MultipleTextFieldGroup.propTypes = {
	items: PropTypes.array,
	onActionUpdate: PropTypes.func,
	onAdd: PropTypes.func,
	onDelete: PropTypes.func,
	onTypeChange: PropTypes.func,
	readOnly: PropTypes.bool,
	isLoading: PropTypes.bool,
	disabled: PropTypes.bool,
	disabledAddButton: PropTypes.bool,
	deleting: PropTypes.bool,
	placeholder: PropTypes.string,
	tooltipMessage: PropTypes.string,
	openTooltip: PropTypes.bool,
	sx: PropTypes.object,
	textFieldStyle: PropTypes.object,
	handleKeyDownText: PropTypes.func,
	simpleValidation: PropTypes.func,
	validation: PropTypes.func,
	showAdvancedSelect: PropTypes.bool,
	accountTypes: PropTypes.array,
	hideTextField: PropTypes.bool,
	warningList: PropTypes.array,
	showDeleteInFirst: PropTypes.bool,
	from: PropTypes.string,
};
